import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Text,
  Flex,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  HStack,
  Progress,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import { 
  FiUsers, 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle, 
  FiStar, 
  FiTrendingUp,
  FiBarChart2,
  FiEye,
  FiChevronRight,
  FiSettings,
  FiFileText
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/api/adminService';
import { getNominationStatusColor, getNominationStatusLabel } from '../../utils/evaluationStatusUtils';

/**
 * Dashboard component
 * Admin dashboard with statistics and overview
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [branchEvaluationProgress, setBranchEvaluationProgress] = useState([]);
  const [evaluationStats, setEvaluationStats] = useState(null);
  const [recentEvaluations, setRecentEvaluations] = useState([]);
  const [committeePerformance, setCommitteePerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const progressBg = useColorModeValue('gray.100', 'gray.700');

  // Fetch statistics on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all data in parallel for better performance
        const [statsData, branchProgressData, evaluationStatsData, recentEvaluationsData, committeePerformanceData] = await Promise.allSettled([
          adminService.getNominationStats(),
          adminService.getBranchEvaluationProgress(),
          adminService.getEvaluationStats(),
          adminService.getRecentEvaluations(5),
          adminService.getCommitteePerformance()
        ]);
        
        // Handle basic nomination stats
        if (statsData.status === 'fulfilled') {
          setStats(statsData.value);
        } else {
          console.error('Error fetching nomination stats:', statsData.reason);
        }
        
        // Handle branch evaluation progress
        if (branchProgressData.status === 'fulfilled') {
          setBranchEvaluationProgress(branchProgressData.value || []);
        } else {
          console.error('Error fetching branch evaluation progress:', branchProgressData.reason);
          setBranchEvaluationProgress([]);
        }
        
        // Handle evaluation statistics
        if (evaluationStatsData.status === 'fulfilled') {
          setEvaluationStats(evaluationStatsData.value);
        } else {
          console.error('Error fetching evaluation stats:', evaluationStatsData.reason);
          setEvaluationStats(null);
        }
        
        // Handle recent evaluations
        if (recentEvaluationsData.status === 'fulfilled') {
          setRecentEvaluations(recentEvaluationsData.value || []);
        } else {
          console.error('Error fetching recent evaluations:', recentEvaluationsData.reason);
          setRecentEvaluations([]);
        }
        
        // Handle committee performance
        if (committeePerformanceData.status === 'fulfilled') {
          setCommitteePerformance(committeePerformanceData.value || []);
        } else {
          console.error('Error fetching committee performance:', committeePerformanceData.reason);
          setCommitteePerformance([]);
        }
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('فشل في تحميل الإحصائيات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Show loading spinner
  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Container>
    );
  }

  // Show error message
  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  // Return early if no stats
  if (!stats) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="info">
          <AlertIcon />
          لا توجد إحصائيات متاحة حالياً.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          لوحة التحكم
        </Heading>
        <Text color="gray.600">
          نظرة عامة على إحصائيات نظام الترشيح
        </Text>
      </Box>

      {/* Enhanced Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="blue.50"
                borderRadius="full"
                color="blue.500"
                me={4}
              >
                <Icon as={FiUsers} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>إجمالي المرشحين</StatLabel>
                <StatNumber>{stats.total_nominations || 0}</StatNumber>
                <StatHelpText>
                  <StatArrow type={(stats.weekly_growth || 0) >= 0 ? "increase" : "decrease"} />
                  {Math.abs(stats.weekly_growth || 0)}% هذا الأسبوع
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="green.50"
                borderRadius="full"
                color="green.500"
                me={4}
              >
                <Icon as={FiCheckCircle} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>المرشحون المقبولون</StatLabel>
                <StatNumber>{stats.by_status?.approved || 0}</StatNumber>
                <StatHelpText>
                  {stats.total_nominations > 0 ? Math.round(((stats.by_status?.approved || 0) / stats.total_nominations) * 100) : 0}% من الإجمالي
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="yellow.50"
                borderRadius="full"
                color="yellow.500"
                me={4}
              >
                <Icon as={FiClock} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>قيد الانتظار</StatLabel>
                <StatNumber>{stats.by_status?.pending || 0}</StatNumber>
                <StatHelpText>
                  {stats.total_nominations > 0 ? Math.round(((stats.by_status?.pending || 0) / stats.total_nominations) * 100) : 0}% من الإجمالي
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="red.50"
                borderRadius="full"
                color="red.500"
                me={4}
              >
                <Icon as={FiAlertCircle} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>المرشحون المرفوضون</StatLabel>
                <StatNumber>{stats.by_status?.rejected || 0}</StatNumber>
                <StatHelpText>
                  {stats.total_nominations > 0 ? Math.round(((stats.by_status?.rejected || 0) / stats.total_nominations) * 100) : 0}% من الإجمالي
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>

        {/* New Evaluation Progress Card */}
        <Card>
          <CardBody>
            <Flex align="center">
              <Box
                p={3}
                bg="purple.50"
                borderRadius="full"
                color="purple.500"
                me={4}
              >
                <Icon as={FiBarChart2} boxSize={6} />
              </Box>
              <Stat>
                <StatLabel>تقدم التقييم</StatLabel>
                <StatNumber>{evaluationStats?.completionRate || 0}%</StatNumber>
                <StatHelpText>
                  {evaluationStats?.totalEvaluated || 0} من {evaluationStats?.totalCandidates || 0} مرشح
                </StatHelpText>
              </Stat>
            </Flex>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Original Branch Stats and Recent Activity - Moved Up */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6} mb={8} alignItems="stretch">
        <GridItem>
          <Card bg={cardBg} shadow="lg" borderWidth="1px" borderColor={borderColor} h="100%">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md" color="blue.600">إحصائيات الفروع</Heading>
                <Badge colorScheme="blue" variant="subtle" px={3} py={1}>
                  {stats?.by_branch?.length || 0} فرع
                </Badge>
              </Flex>
            </CardHeader>
            <CardBody>
              <Stack spacing={5}>
                {stats?.by_branch && stats.by_branch.length > 0 ? (
                  stats.by_branch.map((branch, index) => (
                    <Box key={index} p={4} bg={progressBg} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Flex justify="space-between" align="center" mb={3}>
                        <Box>
                          <Text fontWeight="bold" fontSize="lg" color="gray.700">{branch.name}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {branch.count} مرشح • {branch.percentage}% من الإجمالي
                          </Text>
                        </Box>
                        <Box textAlign="center">
                          <Text fontSize="2xl" fontWeight="bold" color={index === 0 ? 'blue.500' : index === 1 ? 'green.500' : 'purple.500'}>
                            {branch.count}
                          </Text>
                          <Text fontSize="xs" color="gray.400">مرشح</Text>
                        </Box>
                      </Flex>
                      <Progress
                        value={branch.percentage}
                        colorScheme={index === 0 ? 'blue' : index === 1 ? 'green' : 'purple'}
                        borderRadius="full"
                        size="lg"
                        bg="gray.100"
                      />
                    </Box>
                  ))
                ) : (
                  <Flex direction="column" align="center" justify="center" py={8}>
                    <Icon as={FiBarChart2} boxSize={12} color="gray.300" mb={4} />
                    <Text color="gray.500" textAlign="center" fontSize="lg">
                      لا توجد بيانات فروع متاحة
                    </Text>
                  </Flex>
                )}
              </Stack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Flex direction="column" h="100%">
            {/* Recent Activity */}
            <Card bg={cardBg} shadow="lg" borderWidth="1px" borderColor={borderColor} flex="1" display="flex" flexDirection="column">
              <CardHeader>
                <Flex justify="space-between" align="center">
                  <Heading size="md" color="green.600">النشاط الأخير</Heading>
                  <Badge colorScheme="green" variant="subtle" px={2} py={1} fontSize="xs">
                    آخر 4 أنشطة
                  </Badge>
                </Flex>
              </CardHeader>
              <CardBody flex="1" overflowY="auto">
                <Stack spacing={3}>
                  {stats?.recent_activities && stats.recent_activities.length > 0 ? (
                    stats.recent_activities.slice(0, 4).map((activity, index) => (
                      <Box key={index} p={3} bg={progressBg} borderRadius="md" border="1px" borderColor={borderColor}>
                        <Text fontWeight="medium" fontSize="sm" mb={1}>{activity.action}</Text>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="xs" color="gray.500">{activity.date} - {activity.time}</Text>
                          {activity.candidate_name && (
                            <Text fontSize="xs" color="blue.500" fontWeight="medium">({activity.candidate_name})</Text>
                          )}
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Flex direction="column" align="center" justify="center" py={4}>
                      <Icon as={FiClock} boxSize={8} color="gray.300" mb={2} />
                      <Text color="gray.500" textAlign="center" fontSize="sm">
                        لا يوجد نشاط أخير
                      </Text>
                    </Flex>
                  )}
                </Stack>
              </CardBody>
            </Card>

            {/* Quick Actions */}
            <Card bg={cardBg} shadow="lg" borderWidth="1px" borderColor={borderColor} mt={6} flexShrink={0}>
              <CardHeader>
                <Heading size="md" color="purple.600">إجراءات سريعة</Heading>
              </CardHeader>
              <CardBody overflowX="auto" pt={2}>
                <HStack spacing={3}>
                  <Button
                    leftIcon={<FiEye />}
                    colorScheme="blue"
                    variant="outline"
                    size="sm"
                    justifyContent="flex-start"
                    onClick={() => navigate('/admin/candidates')}
                    _hover={{ bg: 'blue.50', transform: 'translateY(-1px)' }}
                    transition="all 0.2s"
                  >
                    عرض جميع المرشحين
                  </Button>
                  <Button
                    leftIcon={<FiUsers />}
                    colorScheme="green"
                    variant="outline"
                    size="sm"
                    justifyContent="flex-start"
                    onClick={() => navigate('/admin/users')}
                    _hover={{ bg: 'green.50', transform: 'translateY(-1px)' }}
                    transition="all 0.2s"
                  >
                    إدارة المستخدمين
                  </Button>
                  <Button
                    leftIcon={<FiFileText />}
                    colorScheme="purple"
                    variant="outline"
                    size="sm"
                    justifyContent="flex-start"
                    onClick={() => navigate('/admin/reports')}
                    _hover={{ bg: 'purple.50', transform: 'translateY(-1px)' }}
                    transition="all 0.2s"
                  >
                    تقارير مفصلة
                  </Button>
                </HStack>
              </CardBody>
            </Card>
          </Flex>
        </GridItem>
      </Grid>

      {/* Evaluation Overview */}
      {evaluationStats && (
        <Card mb={8}>
          <CardHeader>
            <Heading size="md">نظرة عامة على التقييم</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Flex align="center" justify="center" direction="column">
                <CircularProgress 
                  value={evaluationStats.completionRate} 
                  size="120px" 
                  color="blue.400"
                  thickness="8px"
                >
                  <CircularProgressLabel fontSize="lg" fontWeight="bold">
                    {evaluationStats.completionRate}%
                  </CircularProgressLabel>
                </CircularProgress>
                <Text mt={2} fontWeight="medium">معدل الإنجاز</Text>
              </Flex>
              <Box>
                <Stat>
                  <StatLabel>متوسط التقييم العام</StatLabel>
                  <StatNumber color="green.500">{evaluationStats.overallAverage}/10</StatNumber>
                  <StatHelpText>
                    <Icon as={FiTrendingUp} color="green.500" me={1} />
                    أداء ممتاز
                  </StatHelpText>
                </Stat>
              </Box>
              <Box>
                <Stat>
                  <StatLabel>التقييمات المعلقة</StatLabel>
                  <StatNumber color="orange.500">{evaluationStats.pendingEvaluations}</StatNumber>
                  <StatHelpText>بحاجة للمراجعة</StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Branch Evaluation Progress */}
      <Card mb={8}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">تقدم تقييم الفروع</Heading>
            <Badge colorScheme="blue" px={3} py={1}>
              {branchEvaluationProgress.length} فرع
            </Badge>
          </Flex>
        </CardHeader>
        <CardBody>
          <Stack spacing={6}>
            {branchEvaluationProgress.length > 0 ? (
              branchEvaluationProgress.map((branch, index) => (
                <Box key={index} p={4} bg={progressBg} borderRadius="lg">
                  <Flex justify="space-between" align="center" mb={3}>
                    <Box>
                      <Text fontWeight="bold" fontSize="lg">{branch.name}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {branch.evaluatedCandidates} من {branch.totalCandidates} مرشح تم تقييمهم
                      </Text>
                    </Box>
                    <Flex align="center" gap={4}>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="gray.500">متوسط التقييم</Text>
                        <Text fontWeight="bold" color="green.500">{branch.averageScore}/10</Text>
                      </Box>
                      <Box textAlign="center">
                        <Text fontSize="xs" color="gray.500">معدل الإنجاز</Text>
                        <Text fontWeight="bold" color="blue.500">{branch.completionRate}%</Text>
                      </Box>
                    </Flex>
                  </Flex>
                  <Progress 
                    value={branch.completionRate} 
                    colorScheme={branch.completionRate >= 80 ? 'green' : branch.completionRate >= 60 ? 'yellow' : 'red'}
                    borderRadius="full"
                    size="lg"
                  />
                </Box>
              ))
            ) : (
              <Text color="gray.500" textAlign="center">
                لا توجد بيانات فروع متاحة
              </Text>
            )}
          </Stack>
        </CardBody>
      </Card>

      {/* Enhanced Grid Layout */}
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6} mb={8}>
        {/* Recent Evaluations */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">التقييمات الأخيرة</Heading>
              <Button 
                rightIcon={<FiChevronRight />} 
                variant="ghost" 
                colorScheme="blue" 
                size="sm"
              >
                عرض الكل
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <Stack spacing={4}>
              {recentEvaluations.length > 0 ? (
                recentEvaluations.map((evaluation) => (
                  <Flex key={evaluation.id} justify="space-between" align="center" p={3} bg={progressBg} borderRadius="md">
                    <Box>
                      <Text fontWeight="medium">{evaluation.candidateName}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {evaluation.branch} • {evaluation.position}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        بواسطة {evaluation.evaluator}
                      </Text>
                    </Box>
                    <Box textAlign="right">
                      <Flex align="center" gap={2}>
                        <Icon as={FiStar} color="yellow.400" />
                        <Text fontWeight="bold" color="green.500">{evaluation.score}/10</Text>
                      </Flex>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(evaluation.date).toLocaleString('ar-SA', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Text>
                    </Box>
                  </Flex>
                ))
              ) : (
                <Text color="gray.500" textAlign="center">
                  لا توجد تقييمات أخيرة
                </Text>
              )}
            </Stack>
          </CardBody>
        </Card>

        {/* Committee Performance */}
        <Card>
          <CardHeader>
            <Heading size="md">أداء أعضاء اللجنة</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>العضو</Th>
                  <Th isNumeric>التقييمات</Th>
                  <Th isNumeric>المتوسط</Th>
                  <Th>الكفاءة</Th>
                </Tr>
              </Thead>
              <Tbody>
                {committeePerformance.length > 0 ? (
                  committeePerformance.map((member, index) => (
                    <Tr key={index}>
                      <Td fontWeight="medium">{member.name}</Td>
                      <Td isNumeric>{member.evaluationsCount}</Td>
                      <Td isNumeric>
                        <Flex align="center" justify="flex-end">
                          <Icon as={FiStar} color="yellow.400" me={1} boxSize={3} />
                          <Text>{member.averageScore}</Text>
                        </Flex>
                      </Td>
                      <Td>
                        <Badge 
                          colorScheme={member.completionRate >= 90 ? 'green' : member.completionRate >= 80 ? 'yellow' : 'red'}
                          size="sm"
                        >
                          {member.efficiency}
                        </Badge>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={4} textAlign="center" color="gray.500">
                      لا توجد بيانات أداء متاحة
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </Grid>

    </Container>
  );
};

export default Dashboard;
